import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form } from 'react-bootstrap';
import './VoltageDividerCalculatorCore.css';

const VoltageDividerCalculatorCore = ({
  inputVoltage = 5,
  inlineResistor = 3300,
  groundResistor = 4500
}) => {
  // Circuit state
  const [circuit, setCircuit] = useState({
    blankingInputVoltage: inputVoltage,
    factoryBlankingInlineResistor: inlineResistor,
    diodeVoltageDrop: 0.7,
    externalBlankingResistor: groundResistor,
    outputVoltage: 0
  });

  // Diode state (on/off)
  const [isDiodeEnabled, setIsDiodeEnabled] = useState(true);
  const [calculateMode, setCalculateMode] = useState('outputVoltage');

  const formatResistorValue = (value) => {
    if (value >= 1000) {
      return (value / 1000).toFixed(1) + ' kΩ';
    }
    return value + ' Ω';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let parsedValue = parseFloat(value);
    if (value === '' || isNaN(parsedValue)) {
      setCircuit({
        ...circuit,
        [name]: value === '' ? '' : 0
      });
      return;
    }
    switch (name) {
      case 'blankingInputVoltage':
        parsedValue = Math.max(0, Math.min(12, parsedValue));
        break;
      case 'factoryBlankingInlineResistor':
      case 'externalBlankingResistor':
        parsedValue = Math.max(0, Math.min(100000, parsedValue));
        break;
      case 'outputVoltage':
        parsedValue = Math.max(0, Math.min(12, parsedValue));
        break;
      default:
        break;
    }
    setCircuit({
      ...circuit,
      [name]: parsedValue
    });
  };

  const handleCalculateModeChange = (e) => {
    setCalculateMode(e.target.value);
  };

  const handleDiodeToggle = (e) => {
    const isEnabled = e.target.checked;
    setIsDiodeEnabled(isEnabled);
    setCircuit({
      ...circuit,
      diodeVoltageDrop: isEnabled ? 0.7 : 0
    });
  };

  const calculateCircuit = () => {
    const { blankingInputVoltage, factoryBlankingInlineResistor, diodeVoltageDrop, externalBlankingResistor, outputVoltage } = circuit;
    if (blankingInputVoltage === '' || factoryBlankingInlineResistor === '' || 
        externalBlankingResistor === '' || 
        (calculateMode !== 'outputVoltage' && outputVoltage === '')) {
      return;
    }
    const effectiveInputVoltage = blankingInputVoltage - diodeVoltageDrop;
    let updatedCircuit = { ...circuit };
    switch (calculateMode) {
      case 'outputVoltage':
        const calculatedOutputVoltage = effectiveInputVoltage * 
          externalBlankingResistor / (factoryBlankingInlineResistor + externalBlankingResistor);
        updatedCircuit.outputVoltage = parseFloat(calculatedOutputVoltage.toFixed(3));
        break;
      case 'externalBlankingResistor':
        if (outputVoltage >= effectiveInputVoltage) {
          updatedCircuit.outputVoltage = Math.max(0, effectiveInputVoltage);
          updatedCircuit.externalBlankingResistor = 100000;
        } else if (outputVoltage <= 0) {
          updatedCircuit.outputVoltage = 0.001;
          updatedCircuit.externalBlankingResistor = 0;
        } else {
          const calculatedR10 = (factoryBlankingInlineResistor * outputVoltage) / 
                               (effectiveInputVoltage - outputVoltage);
          updatedCircuit.externalBlankingResistor = Math.round(calculatedR10);
        }
        break;
      case 'factoryBlankingInlineResistor':
        if (outputVoltage >= effectiveInputVoltage) {
          updatedCircuit.outputVoltage = Math.max(0, effectiveInputVoltage);
          updatedCircuit.factoryBlankingInlineResistor = 0;
        } else if (outputVoltage <= 0) {
          updatedCircuit.outputVoltage = 0.001;
          updatedCircuit.factoryBlankingInlineResistor = 100000;
        } else {
          const calculatedR11 = externalBlankingResistor * 
                              (effectiveInputVoltage - outputVoltage) / outputVoltage;
          updatedCircuit.factoryBlankingInlineResistor = Math.round(calculatedR11);
        }
        break;
      default:
        break;
    }
    setCircuit(updatedCircuit);
  };

  useEffect(() => {
    calculateCircuit();
  }, [circuit.blankingInputVoltage, circuit.diodeVoltageDrop, 
      circuit.factoryBlankingInlineResistor, circuit.externalBlankingResistor, 
      calculateMode === 'outputVoltage' ? null : circuit.outputVoltage]);

  const renderBlankingSVG = () => {
    const factoryResistorValue = formatResistorValue(circuit.factoryBlankingInlineResistor);
    const externalResistorValue = formatResistorValue(circuit.externalBlankingResistor);
    const outputVoltageDisplay = (typeof circuit.outputVoltage === 'number') 
      ? circuit.outputVoltage.toFixed(3) 
      : '0.000';
    return (
      <svg className="svg-diagram" viewBox="0 0 600 400" preserveAspectRatio="xMidYMid meet">
        {/* Input Voltage Label */}
        <text x="60" y="150" className="voltage-divider-label" textAnchor="middle" fontWeight="bold">V BLANKING</text>
        <text x="60" y="170" className="voltage-divider-label small-text" textAnchor="middle" fontSize="10">input</text>
        
        {/* Input Junction with Red Circle */}
        <circle cx="120" cy="160" r="5" fill="white" />
        
        {/* Input Horizontal Line start */}
        <line x1="120" y1="160" x2="160" y2="160" className="wire" />
        
        {isDiodeEnabled ? (
          // Diode when enabled
          <>
            {/* Diode Symbol - Reversed direction */}
            <polygon points="180,160 160,150 160,170" fill="red" className="diode-symbol" />
            <line x1="160" y1="150" x2="160" y2="170" className="wire" />
            <line x1="180" y1="160" x2="190" y2="160" className="wire" />
            
            {/* Diode vertical line */}
            <line x1="180" y1="150" x2="180" y2="170" stroke="#ffffff" strokeWidth="3" />
            
            {/* Input Horizontal Line after diode */}
            <line x1="190" y1="160" x2="210" y2="160" className="wire" />
        
            {/* Display diode voltage drop */}
            <text x="170" y="200" className="resistor-label" textAnchor="middle">
              R9: 0.7V
            </text>
          </>
        ) : (
          // Direct line when diode is disabled
          <>
            <line x1="160" y1="160" x2="210" y2="160" className="wire" strokeWidth="3" />
          </>
        )}
        
        {/* Factory Resistor Label */}
        <text x="260" y="110" className="resistor-label" textAnchor="middle">inline resistor</text>
        <text x="260" y="130" className="resistor-label" textAnchor="middle">R11: {factoryResistorValue}</text>
        
        {/* Factory Resistor Symbol - Horizontal Zigzag */}
        <path d="M210,160 h20 l5,-10 l10,20 l10,-20 l10,20 l10,-20 l10,20 l10,-20 l5,10 h20" className="resistor-zigzag" />
        
        {/* Junction Dot with Red Circle */}
        <circle cx="340" cy="160" r="5" fill="white" />
        
        {/* Output Horizontal Line */}
        <line x1="320" y1="160" x2="480" y2="160" className="wire" />
        
        {/* Output Junction with Red Circle */}
        <circle cx="480" cy="160" r="5" fill="white" />
        
        {/* Output Voltage Label */}
        <text x="540" y="150" className="voltage-divider-label" textAnchor="middle" fontWeight="bold">V BLANKING</text>
        <text x="540" y="170" className="voltage-divider-label small-text" textAnchor="middle" fontSize="10">output</text>
        
        {/* Output Calculated Voltage Value */}
        <text x="540" y="200" className="resistor-label" textAnchor="middle">{outputVoltageDisplay}V</text>
        
        {/* Vertical Line to Ground Resistor - Moved up by 10 */}
        <line x1="320" y1="160" x2="340" y2="160" className="wire" />
        <line x1="340" y1="160" x2="340" y2="190" className="wire" />
        
        {/* External Blanking Resistor Label - Moved up by 10 */}
        <text x="420" y="210" className="resistor-label" textAnchor="middle">ground</text>
        <text x="420" y="230" className="resistor-label" textAnchor="middle">resistor</text>
        <text x="420" y="250" className="resistor-label" textAnchor="middle">R10: {externalResistorValue}</text>
        
        {/* External Blanking Resistor Symbol - Vertical Zigzag - Moved up by 10 */}
        <path d="M320,190 v10 l-10,5 l20,10 l-20,10 l20,10 l-20,10 l20,10 l-20,10 l10,5 v10" className="resistor-zigzag" transform="translate(20, 0)" />
        
        {/* Connecting wire from R10 to ground */}
        <line x1="340" y1="280" x2="340" y2="300" className="wire" />
        
        {/* Ground Symbol - Moved 20 points lower */}
        <line x1="330" y1="300" x2="350" y2="300" className="ground-symbol" />
        <line x1="333" y1="307" x2="347" y2="307" className="ground-symbol" />
        <line x1="336" y1="314" x2="344" y2="314" className="ground-symbol" />
      </svg>
    );
  };

  return (
    <Row>
      <Col md={6}>
        <Card className="form-card">
          <Card.Header as="h5">
            <Row>
              <Col>Blanking Resistor Calculator</Col>
            </Row>
          </Card.Header>
          <Card.Body className="form-card-body">
            <Form className="calculator-form">
              <div className="calculator-controls">
                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Calculate:</Form.Label>
                      <Form.Select
                        name="calculateMode"
                        value={calculateMode}
                        onChange={handleCalculateModeChange}
                        className="calculate-mode-dropdown"
                      >
                        <option value="outputVoltage">Output Voltage</option>
                        <option value="externalBlankingResistor">Ground Resistor (R10)</option>
                        <option value="factoryBlankingInlineResistor">Inline Resistor (R11)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Group className="mb-0">
                      <div className="diode-toggle-input">
                        <Form.Check 
                          type="switch"
                          id="diode-toggle"
                          label={`R9: Diode ${isDiodeEnabled ? 'ON - 0.7V drop' : 'OFF - 0V'}`}
                          checked={isDiodeEnabled}
                          onChange={handleDiodeToggle}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Blanking Input Voltage (V)</Form.Label>
                      <Form.Control 
                        type="number" 
                        name="blankingInputVoltage" 
                        value={circuit.blankingInputVoltage}
                        onChange={handleInputChange}
                        step="0.1"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Inline Resistor (Ω) R11</Form.Label>
                      <Form.Control 
                        type="number" 
                        name="factoryBlankingInlineResistor" 
                        value={circuit.factoryBlankingInlineResistor}
                        onChange={handleInputChange}
                        disabled={calculateMode === 'factoryBlankingInlineResistor'}
                        className={calculateMode === 'factoryBlankingInlineResistor' ? 'calculated-field' : ''}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ground Resistor (Ω) R10</Form.Label>
                      <Form.Control 
                        type="number" 
                        name="externalBlankingResistor" 
                        value={circuit.externalBlankingResistor}
                        onChange={handleInputChange}
                        disabled={calculateMode === 'externalBlankingResistor'}
                        className={calculateMode === 'externalBlankingResistor' ? 'calculated-field' : ''}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Desired Output Voltage (V)</Form.Label>
                      <Form.Control 
                        type="number" 
                        name="outputVoltage" 
                        value={circuit.outputVoltage}
                        onChange={handleInputChange}
                        disabled={calculateMode === 'outputVoltage'}
                        className={calculateMode === 'outputVoltage' ? 'calculated-field' : ''}
                        step="0.1"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Col>
      <Col md={6}>
        <Card className="diagram-card" style={{ height: '100%' }}>
          <Card.Header as="h5">Blanking Voltage Divider</Card.Header>
          <Card.Body>
            <div className="circuit-container">
              <div className="circuit-diagram">
                {renderBlankingSVG()}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default VoltageDividerCalculatorCore; 